import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ROLES, type Role, type UserDetail, type UserListItem } from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';
import {
  useUserActivate,
  useUserDeactivate,
  useUserDetail,
  useUsersList,
  type UserListFilter,
} from './api';
import { EmployeeFormModal } from './EmployeeFormModal';

export function EmployeesPage(): ReactNode {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [activeFilter, setActiveFilter] = useState<UserListFilter['active']>('true');
  const [roleFilter, setRoleFilter] = useState<'' | Role>('');
  const [search, setSearch] = useState('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const filter: UserListFilter = useMemo(
    () => ({
      active: activeFilter,
      role: roleFilter || undefined,
      search: search.trim() || undefined,
    }),
    [activeFilter, roleFilter, search],
  );

  const { data: users = [], isLoading, error } = useUsersList(filter);
  const detail = useUserDetail(editingId);
  const deactivate = useUserDeactivate();
  const activate = useUserActivate();

  const onToggleActive = async (u: UserListItem) => {
    setActionError(null);
    try {
      if (u.active) await deactivate.mutateAsync(u.id);
      else await activate.mutateAsync(u.id);
    } catch (err) {
      setActionError(extractApiError(err));
    }
  };

  const onCreate = () => {
    setEditingId(null);
    setCreateOpen(true);
  };

  const editingUser: UserDetail | null =
    editingId !== null && detail.data ? detail.data : null;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">{t('employees.title')}</h1>
          <p className="mt-1 text-sm text-zinc-500">{t('employees.subtitle')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={onCreate}>
          {t('employees.actions.add')}
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3 rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-zinc-500">{t('common.filter.status')}:</span>
          {(['true', 'false', 'all'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setActiveFilter(opt)}
              className={`rounded-md px-3 py-1.5 text-sm transition ${
                activeFilter === opt
                  ? 'bg-brand-100 text-brand-700'
                  : 'text-zinc-600 hover:bg-zinc-100'
              }`}
            >
              {t(
                `common.filter.${
                  opt === 'true' ? 'activeOnly' : opt === 'false' ? 'inactiveOnly' : 'all'
                }`,
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-1 flex-wrap items-center gap-3">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter((e.target.value as Role) || '')}
            className="input max-w-[12rem]"
          >
            <option value="">{t('employees.filter.allRoles')}</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`roles.${r}`)}
              </option>
            ))}
          </select>
          <input
            type="search"
            placeholder={t('employees.filter.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input flex-1 min-w-[12rem]"
          />
        </div>
      </div>

      {actionError && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {actionError}
        </div>
      )}
      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {extractApiError(error)}
        </div>
      )}

      <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-zinc-200">
        <table className="min-w-full divide-y divide-zinc-200">
          <thead className="bg-zinc-50">
            <tr className="text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3">{t('employees.table.name')}</th>
              <th className="px-4 py-3">{t('employees.table.email')}</th>
              <th className="px-4 py-3">{t('employees.table.role')}</th>
              <th className="px-4 py-3">{t('employees.table.services')}</th>
              <th className="px-4 py-3">{t('employees.table.status')}</th>
              <th className="px-4 py-3 text-right">{t('employees.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-zinc-400">
                  {t('common.loading')}
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-zinc-400">
                  {t('employees.empty')}
                </td>
              </tr>
            ) : (
              users.map((u) => {
                const isSelf = currentUser?.id === u.id;
                return (
                  <tr key={u.id} className={u.active ? '' : 'bg-zinc-50/60'}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span
                          className="inline-block h-3 w-3 rounded-full ring-1 ring-zinc-200"
                          style={{ backgroundColor: u.color ?? '#d4d4d8' }}
                          title={u.color ?? undefined}
                        />
                        <div>
                          <div className="font-medium text-zinc-900">
                            {u.firstName} {u.lastName}
                            {isSelf && (
                              <span className="ml-2 rounded bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700">
                                {t('employees.you')}
                              </span>
                            )}
                          </div>
                          {u.bio && (
                            <div className="mt-0.5 max-w-md truncate text-xs text-zinc-500" title={u.bio}>
                              {u.bio}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{u.email}</td>
                    <td className="px-4 py-3 text-zinc-700">
                      <div className="flex items-center gap-2">
                        <span>{t(`roles.${u.role}`)}</span>
                        {u.bookable && (
                          <span
                            className="inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 ring-1 ring-amber-200"
                            title={t('employees.bookableBadgeHint')}
                          >
                            {t('employees.bookableBadge')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">
                      {u.bookable ? u.servicesCount : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {u.active ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
                          {t('common.status.active')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500 ring-1 ring-zinc-200">
                          {t('common.status.inactive')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          className="text-sm text-brand-600 hover:text-brand-700"
                          onClick={() => setEditingId(u.id)}
                        >
                          {t('common.actions.edit')}
                        </button>
                        <button
                          type="button"
                          className="text-sm text-zinc-500 hover:text-zinc-800 disabled:cursor-not-allowed disabled:text-zinc-300"
                          onClick={() => void onToggleActive(u)}
                          disabled={isSelf}
                          title={isSelf ? t('employees.cannotDeactivateSelf') : undefined}
                        >
                          {u.active
                            ? t('common.actions.deactivate')
                            : t('common.actions.activate')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <EmployeeFormModal
        open={createOpen}
        user={null}
        currentUserId={currentUser?.id ?? -1}
        onClose={() => setCreateOpen(false)}
      />
      <EmployeeFormModal
        open={editingUser !== null}
        user={editingUser}
        currentUserId={currentUser?.id ?? -1}
        onClose={() => setEditingId(null)}
      />
    </div>
  );
}
